// src/pages/RegistroRientriMaterialiPage.js
// Vista gestionale aggregata di tutti i Rientri Materiali dell'organizzazione — sola
// consultazione con filtri; la creazione avviene solo da Acquisti → Rientro Materiali.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { formatValutaItaliana } from "../utils/clienteHelpers";
import { Search, RotateCcw, Trash2, X, Printer, Paperclip, FileText } from "lucide-react";
import { DEFAULT_TIPO_RESO } from "../utils/rientroMaterialiConstants";
import { dominioSito } from "../utils/fornitore";

const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};
const lbl = {fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",display:"block",marginBottom:2};
const val = {fontSize:13,fontWeight:600,color:"#111827"};

export default function RegistroRientriMaterialiPage({ deepLink }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [rientri, setRientri] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [dettaglio, setDettaglio] = useState(null);

  const [cerca, setCerca] = useState("");
  const [filtroCommessa, setFiltroCommessa] = useState(deepLink?.projectId || "");
  const [filtroCliente, setFiltroCliente] = useState(deepLink?.clienteId || "");
  const [filtroTipoReso, setFiltroTipoReso] = useState("");
  const [filtroValore, setFiltroValore] = useState("");
  const [clienti, setClienti] = useState([]);

  // Se si arriva qui da "Relazioni della Commessa" o dalla Scheda Cliente, precompila il filtro
  useEffect(() => { if (deepLink?.projectId) setFiltroCommessa(deepLink.projectId); }, [deepLink?.projectId]);
  useEffect(() => { if (deepLink?.clienteId) setFiltroCliente(deepLink.clienteId); }, [deepLink?.clienteId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"rientriMateriali"),where("organizationId","==",effectiveOrgId)),
      snap=>setRientri(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent)));
  },[effectiveOrgId]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ragioneSociale||"").localeCompare(b.ragioneSociale||"","it"))));
  },[effectiveOrgId]);

  // Rientro Materiali non ha un campo clienteId diretto (è sempre agganciato a una commessa,
  // mai al solo cliente) — il cliente si ricava dalla commessa collegata, dato già presente.
  const clienteDellaCommessa = pId => commesse.find(c=>c.id===pId)?.clienteId || "";

  // Elenco tipi di reso realmente usati nei rientri esistenti + i default, per il filtro
  const tipiResoDisponibili = [...new Set([...DEFAULT_TIPO_RESO, ...rientri.map(r=>r.tipoReso).filter(Boolean)])];

  const filtrati = rientri.filter(r=>{
    if(filtroCommessa && r.projectId!==filtroCommessa) return false;
    if(filtroCliente && clienteDellaCommessa(r.projectId)!==filtroCliente) return false;
    if(filtroTipoReso && r.tipoReso!==filtroTipoReso) return false;
    if(filtroValore==="noto" && !r.valoreNoto) return false;
    if(filtroValore==="non_noto" && r.valoreNoto) return false;
    if(cerca){
      const q = cerca.toLowerCase();
      return (r.numero||"").toLowerCase().includes(q) || (r.descrizione||"").toLowerCase().includes(q) ||
        (r.projectName||"").toLowerCase().includes(q) || (r.compilatoreNome||"").toLowerCase().includes(q) ||
        (r.clienteNome||"").toLowerCase().includes(q);
    }
    return true;
  });

  const totaleScalato = filtrati.reduce((s,r)=> s + (r.valoreNoto ? r.quantita*r.prezzoUnitarioApplicato : 0), 0);

  const resetFiltri = () => { setCerca("");setFiltroCommessa("");setFiltroCliente("");setFiltroTipoReso("");setFiltroValore(""); };
  const filtriAttivi = cerca||filtroCommessa||filtroCliente||filtroTipoReso||filtroValore;

  const elimina = async (r) => {
    if(!await confirm(`Eliminare il rientro ${r.numero}? ${r.movimentoEconomicoId?"Verrà rimosso anche il relativo movimento economico.":""}`,{type:"danger"})) return;
    if(r.movimentoEconomicoId){ try{ await deleteDoc(doc(db,"movimentiEconomici",r.movimentoEconomicoId)); }catch{} }
    await deleteDoc(doc(db,"rientriMateriali",r.id));
    addToast("Rientro eliminato","info");
    setDettaglio(null);
  };

  const stampaPDF = (r) => {
    const win = window.open("","_blank");
    const condizioni = (r.condizioniRitiro||[]).length ? (r.condizioniRitiro||[]).map(c=>`<li>${c}</li>`).join("") : "<li style='color:#9ca3af'>Nessuna indicata</li>";
    const allegati = (r.allegati||[]).length ? (r.allegati||[]).map(a=>`<li>${a.nome}</li>`).join("") : "<li style='color:#9ca3af'>Nessuno</li>";
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Rientro Materiali ${r.numero||""} — ${r.descrizione||""}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:14mm 16mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-start;}
      .info{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin-bottom:16px;}
      .lbl{font-size:9px;color:#9ca3af;font-weight:700;text-transform:uppercase;display:block;margin-bottom:2px;}
      .val{font-size:12px;font-weight:600;}
      .sez{margin-bottom:14px;}
      .sez h4{font-size:10px;color:#7c3aed;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px;border-bottom:1px solid #ede9fe;padding-bottom:3px;}
      ul{padding-left:18px;} li{margin-bottom:2px;}
      .footer{border-top:1px solid #e5e7eb;margin-top:16px;padding-top:8px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>
        <div style="font-size:16px;font-weight:800;color:#1e1b4b">RIENTRO MATERIALI</div>
        <div style="font-size:18px;font-weight:900;color:#7c3aed;font-family:monospace">${r.numero||"—"}</div>
      </div>
      <span style="padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700;background:${r.valoreNoto?"#d1fae5":"#fef3c7"};color:${r.valoreNoto?"#065f46":"#92400e"}">${r.valoreNoto?`Valore applicato: -€ ${formatValutaItaliana(r.quantita*r.prezzoUnitarioApplicato)}`:"Valore economico non disponibile"}</span>
    </div>
    <div class="info">
      <div><span class="lbl">Commessa</span><span class="val">${r.codiceCommessa?`[${r.codiceCommessa}] `:""}${r.projectName||"—"}</span></div>
      <div><span class="lbl">Cliente</span><span class="val">${r.clienteNome||"—"}</span></div>
      <div><span class="lbl">PM Commessa</span><span class="val">${r.pmNome||"—"}</span></div>
      <div><span class="lbl">Materiale rientrato</span><span class="val">${r.descrizione||"—"}</span></div>
      <div><span class="lbl">Quantità</span><span class="val">${r.quantita||"—"} ${r.unitaMisura||""}</span></div>
      <div><span class="lbl">Data rientro</span><span class="val">${r.dataRientro||"—"}</span></div>
      <div><span class="lbl">Registrato da</span><span class="val">${r.compilatoreNome||"—"}</span></div>
      <div><span class="lbl">Tipo di reso</span><span class="val">${r.tipoReso||"—"}</span></div>
      <div><span class="lbl">Automezzo suggerito</span><span class="val">${r.automezzoSuggerito||"—"}</span></div>
      <div><span class="lbl">Tempistiche</span><span class="val">${r.tempistiche||"—"}</span></div>
      <div><span class="lbl">Referente di Cantiere</span><span class="val">${r.referenteCantiere||"—"}</span></div>
      ${r.indirizzoRitiro?`<div><span class="lbl">Indirizzo di ritiro</span><span class="val">${r.indirizzoRitiro}</span></div>`:""}
    </div>
    ${r.indicazioniAccesso?`<div class="sez"><h4>Indicazioni per l'accesso</h4><div>${r.indicazioniAccesso}</div></div>`:""}
    <div class="sez"><h4>Condizioni per il ritiro</h4><ul>${condizioni}</ul></div>
    ${r.note?`<div class="sez"><h4>Note</h4><div>${r.note}</div></div>`:""}
    <div class="sez"><h4>Allegati</h4><ul>${allegati}</ul></div>
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
              <RotateCcw size={18}/> Registro Rientri Materiali
            </h2>
            <p style={{fontSize:12,color:"#9ca3af",margin:"2px 0 0"}}>
              {filtrati.length} rientro/i · -€ {formatValutaItaliana(totaleScalato)} scalati dal Motore Economico · Vista gestionale — la creazione avviene solo da Acquisti → Rientro Materiali
            </p>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <Search size={13} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
            <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca numero, materiale, commessa..." style={{...inp,paddingLeft:26,width:220}}/>
          </div>
          <select value={filtroCliente} onChange={e=>setFiltroCliente(e.target.value)} style={inp}>
            <option value="">Tutti i clienti</option>
            {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
          </select>
          <select value={filtroCommessa} onChange={e=>setFiltroCommessa(e.target.value)} style={inp}>
            <option value="">Tutte le commesse</option>
            {commesse.map(c=><option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}</option>)}
          </select>
          <select value={filtroTipoReso} onChange={e=>setFiltroTipoReso(e.target.value)} style={inp}>
            <option value="">Tutti i tipi di reso</option>
            {tipiResoDisponibili.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filtroValore} onChange={e=>setFiltroValore(e.target.value)} style={inp}>
            <option value="">Valore economico: tutti</option>
            <option value="noto">✓ Valore noto (applicato)</option>
            <option value="non_noto">⚠️ Non disponibile</option>
          </select>
          {filtriAttivi && (
            <button onClick={resetFiltri} style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>× Reset</button>
          )}
        </div>
      </div>

      <div style={{flex:1,overflow:"auto"}}>
        {filtrati.length===0 ? (
          <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
            <div style={{fontSize:48,marginBottom:12,opacity:.15}}>🔄</div>
            <div style={{fontSize:14,fontWeight:600}}>Nessun rientro trovato</div>
          </div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
            <thead style={{position:"sticky",top:0,zIndex:5}}>
              <tr style={{background:"#1e1b4b"}}>
                {["N°","Data","Commessa","Materiale","Tipo di reso","Quantità","Valore economico","Registrato da",""].map(h=>(
                  <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map((r,i)=>(
                <tr key={r.id} onClick={()=>setDettaglio(r)} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}}>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",fontWeight:800,color:"#7c3aed"}}>{r.numero}</td>
                  <td style={{padding:"8px 12px",color:"#6b7280",whiteSpace:"nowrap"}}>{r.dataRientro?.split("-").reverse().join("/")}</td>
                  <td style={{padding:"8px 12px",color:"#374151"}}>{r.codiceCommessa && `[${r.codiceCommessa}] `}{r.projectName}{r.clienteNome && <span style={{color:"#9ca3af"}}> · {r.clienteNome}</span>}</td>
                  <td style={{padding:"8px 12px",fontWeight:600,color:"#111827"}}>{r.descrizione}</td>
                  <td style={{padding:"8px 12px",color:"#6b7280",fontSize:11.5}}>{r.tipoReso||"—"}</td>
                  <td style={{padding:"8px 12px"}}>{r.quantita} {r.unitaMisura}</td>
                  <td style={{padding:"8px 12px"}}>
                    {r.valoreNoto ? (
                      <span style={{fontSize:11,fontWeight:700,color:"#059669"}}>✓ -€ {formatValutaItaliana(r.quantita*r.prezzoUnitarioApplicato)}</span>
                    ) : (
                      <span style={{fontSize:11,fontWeight:700,color:"#d97706"}}>⚠️ non disponibile</span>
                    )}
                  </td>
                  <td style={{padding:"8px 12px",color:"#6b7280"}}>{r.compilatoreNome}</td>
                  <td style={{padding:"8px 12px",display:"flex",gap:10}}>
                    <button onClick={e=>{e.stopPropagation();stampaPDF(r);}} title="Stampa PDF" style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><Printer size={14}/></button>
                    {(canAdmin || r.compilatoreUid===currentUser?.uid) && (
                      <button onClick={e=>{e.stopPropagation();elimina(r);}} title="Elimina" style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={14}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dettaglio && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setDettaglio(null)}>
          <div className="modal" style={{maxWidth:600,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                <RotateCcw size={16} color="#7c3aed"/> Rientro {dettaglio.numero}
              </h3>
              <button onClick={()=>setDettaglio(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
            </div>

            <div style={{marginBottom:14,padding:"9px 12px",borderRadius:8,fontSize:12,
              background: dettaglio.valoreNoto?"#f0fdf4":"#fffbeb", border:`1px solid ${dettaglio.valoreNoto?"#bbf7d0":"#fde68a"}`,
              color: dettaglio.valoreNoto?"#065f46":"#92400e", fontWeight:600}}>
              {dettaglio.valoreNoto
                ? `✓ Valore economico applicato: -€ ${formatValutaItaliana(dettaglio.quantita*dettaglio.prezzoUnitarioApplicato)} (fonte: ${dettaglio.fonteValore==="listino"?"Listino Materiali":"movimento economico già registrato"})`
                : "⚠️ Valore economico non disponibile — non applicato al Motore Economico"}
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div><label style={lbl}>Commessa</label><span style={val}>{dettaglio.codiceCommessa&&`[${dettaglio.codiceCommessa}] `}{dettaglio.projectName}</span></div>
              <div><label style={lbl}>Cliente</label><span style={val}>{dettaglio.clienteNome||"—"}</span></div>
              <div><label style={lbl}>PM Commessa</label><span style={val}>{dettaglio.pmNome||"—"}</span></div>
              <div><label style={lbl}>Materiale rientrato</label><span style={val}>{dettaglio.descrizione}</span></div>
              <div><label style={lbl}>Quantità</label><span style={val}>{dettaglio.quantita} {dettaglio.unitaMisura}</span></div>
              <div><label style={lbl}>Data rientro</label><span style={val}>{dettaglio.dataRientro?.split("-").reverse().join("/")}</span></div>
              <div><label style={lbl}>Registrato da</label><span style={val}>{dettaglio.compilatoreNome}</span></div>
              <div><label style={lbl}>Tipo di reso</label><span style={val}>{dettaglio.tipoReso||"—"}</span></div>
              <div><label style={lbl}>Automezzo suggerito</label><span style={val}>{dettaglio.automezzoSuggerito||"—"}</span></div>
              <div><label style={lbl}>Tempistiche</label><span style={val}>{dettaglio.tempistiche||"—"}</span></div>
              <div><label style={lbl}>Referente di Cantiere</label><span style={val}>{dettaglio.referenteCantiere||"—"}</span></div>
              {dettaglio.indirizzoRitiro && (
                <div><label style={lbl}>Indirizzo di ritiro</label><span style={val}>{dettaglio.indirizzoRitiro}</span></div>
              )}
            </div>

            {dettaglio.indicazioniAccesso && (
              <div style={{marginBottom:14}}>
                <label style={lbl}>Indicazioni per l'accesso</label>
                <div style={{fontSize:13,color:"#374151"}}>{dettaglio.indicazioniAccesso}</div>
              </div>
            )}

            <div style={{marginBottom:14}}>
              <label style={lbl}>Condizioni per il ritiro</label>
              {(dettaglio.condizioniRitiro||[]).length ? (
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                  {dettaglio.condizioniRitiro.map(c=>(
                    <span key={c} style={{fontSize:11,padding:"3px 10px",borderRadius:12,background:"#ede9fe",color:"#7c3aed",fontWeight:600}}>{c}</span>
                  ))}
                </div>
              ) : <span style={{fontSize:12,color:"#9ca3af"}}>Nessuna indicata</span>}
            </div>

            {dettaglio.note && (
              <div style={{marginBottom:14}}>
                <label style={lbl}>Note</label>
                <div style={{fontSize:13,color:"#374151"}}>{dettaglio.note}</div>
              </div>
            )}

            <div style={{marginBottom:16}}>
              <label style={lbl}>Allegati</label>
              {(dettaglio.allegati||[]).length ? (
                <div style={{display:"grid",gap:6,marginTop:4}}>
                  {dettaglio.allegati.map((a,i)=>(
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#2563eb",textDecoration:"none"}}>
                      {a.tipo==="foto" ? <Paperclip size={12}/> : <FileText size={12}/>} {a.nome}
                    </a>
                  ))}
                </div>
              ) : <span style={{fontSize:12,color:"#9ca3af"}}>Nessuno</span>}
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"space-between"}}>
              <div>
                {(canAdmin || dettaglio.compilatoreUid===currentUser?.uid) && (
                  <button onClick={()=>elimina(dettaglio)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",display:"flex",alignItems:"center",gap:5,fontSize:12}}>
                    <Trash2 size={13}/> Elimina
                  </button>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-secondary" onClick={()=>setDettaglio(null)}>Chiudi</button>
                <button className="btn btn-primary" onClick={()=>stampaPDF(dettaglio)} style={{display:"flex",alignItems:"center",gap:6}}>
                  <Printer size={14}/> Stampa PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
